import {
  type KnowledgeSection,
  type KnowledgeView,
  type KnowledgeViewKey,
  type NarrationSource,
  type RelatedPage,
  type SectionOrientation,
  type UserConfidence,
  type ViewNavigation,
} from "./types";

/**
 * Normalise the raw `GET /v1/runs/{id}/views` payload (the backend
 * `knowledge-view-navigation` schema) into the {@link ViewNavigation} the UI
 * consumes. Everything is parsed defensively so a partially-generated or older
 * run still renders something coherent.
 */
export function adaptViewNavigation(input: unknown): ViewNavigation {
  if (!isRecord(input)) {
    throw new Error("View navigation payload is not an object");
  }
  const rawViews = Array.isArray(input.views) ? input.views : [];
  const views = rawViews.map(adaptView).filter((v): v is KnowledgeView => v !== null);
  const defaultView = pickDefaultView(input.default_view, views);
  return {
    schemaVersion: numberOr(input.schema_version, 1),
    provider: stringOr(input.provider, "aws"),
    ...(typeof input.generated_at === "string" ? { generatedAt: input.generated_at } : {}),
    narrated: Boolean(input.narrated),
    defaultView,
    views,
    relatedPages: adaptRelatedPages(input.related_pages),
    errors: adaptErrors(input.errors),
  };
}

/**
 * Cross-cutting pages, keeping only entries with both a key and a path: a page
 * without a path is not reachable, and rendering it would produce a dead link.
 */
function adaptRelatedPages(raw: unknown): readonly RelatedPage[] {
  if (!Array.isArray(raw)) return [];
  const pages: RelatedPage[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const key = typeof entry.key === "string" ? entry.key : "";
    const path = typeof entry.path === "string" ? entry.path : "";
    if (!key || !path) continue;
    pages.push({
      key,
      path,
      title: stringOr(entry.title, titleCase(key)),
      description: stringOr(entry.description, ""),
    });
  }
  return pages;
}

function adaptErrors(raw: unknown): Readonly<Record<string, string>> {
  if (!isRecord(raw)) return {};
  const errors: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string" && value.length > 0) errors[key] = value;
  }
  return errors;
}

function adaptView(raw: unknown): KnowledgeView | null {
  if (!isRecord(raw)) return null;
  const key = typeof raw.view === "string" ? (raw.view as KnowledgeViewKey) : null;
  if (!key) return null;
  const rawSections = Array.isArray(raw.sections) ? raw.sections : [];
  const sections = rawSections.map(adaptSection).filter((s): s is KnowledgeSection => s !== null);
  return {
    key,
    displayName: stringOr(raw.display_name, titleCase(key)),
    audience: stringOr(raw.audience, ""),
    depth: stringOr(raw.depth, "balanced"),
    confidenceDisplay: confidenceDisplayOf(raw.confidence_display),
    overallConfidence: confidenceOf(raw.overall_confidence),
    sectionCount: numberOr(raw.section_count, sections.length),
    personaQuestions: stringArray(raw.persona_questions),
    ...(typeof raw.output_relpath === "string" ? { outputRelpath: raw.output_relpath } : {}),
    ...(typeof raw.content_hash === "string" ? { contentHash: raw.content_hash } : {}),
    ...(typeof raw.ai_section_count === "number" ? { aiSectionCount: raw.ai_section_count } : {}),
    ...(typeof raw.fallback_section_count === "number"
      ? { fallbackSectionCount: raw.fallback_section_count }
      : {}),
    ...(typeof raw.empty_section_count === "number"
      ? { emptySectionCount: raw.empty_section_count }
      : {}),
    sections,
    ...(typeof raw.error === "string" ? { error: raw.error } : {}),
  };
}

function adaptSection(raw: unknown): KnowledgeSection | null {
  if (!isRecord(raw)) return null;
  const key = typeof raw.key === "string" ? raw.key : null;
  if (!key) return null;
  const title = stringOr(raw.title, titleCase(key));
  const source = narrationSourceOf(raw.source);
  return {
    key,
    title,
    anchor: stringOr(raw.anchor, slugify(title)),
    orientation: orientationOf(raw.orientation),
    confidence: confidenceOf(raw.confidence),
    empty: Boolean(raw.empty),
    entityCount: numberOr(raw.entity_count, 0),
    subsections: stringArray(raw.subsections),
    questions: stringArray(raw.questions),
    ...(typeof raw.group === "string" && raw.group.length > 0 ? { group: raw.group } : {}),
    ...(typeof raw.path === "string" ? { path: raw.path } : {}),
    ...(typeof raw.order === "number" ? { order: raw.order } : {}),
    ...(source !== null ? { source } : {}),
    ...(typeof raw.kqi_score === "number" ? { kqiScore: raw.kqi_score } : {}),
    ...(typeof raw.needs_improvement === "boolean"
      ? { needsImprovement: raw.needs_improvement }
      : {}),
    ...(typeof raw.content_hash === "string" ? { contentHash: raw.content_hash } : {}),
    ...(typeof raw.temporal_changes === "number" ? { temporalChanges: raw.temporal_changes } : {}),
    ...(typeof raw.temporal_statements === "number"
      ? { temporalStatements: raw.temporal_statements }
      : {}),
  };
}

function pickDefaultView(raw: unknown, views: readonly KnowledgeView[]): KnowledgeViewKey {
  if (typeof raw === "string" && views.some((v) => v.key === raw)) {
    return raw as KnowledgeViewKey;
  }
  return views[0]?.key ?? "generic";
}

// -- coercion helpers ------------------------------------------------------

function confidenceOf(raw: unknown): UserConfidence {
  if (raw === "Verified" || raw === "Likely" || raw === "Assumed" || raw === "Unknown") {
    return raw;
  }
  return "Unknown";
}

function confidenceDisplayOf(raw: unknown): "minimal" | "summary" | "prominent" {
  if (raw === "minimal" || raw === "summary" || raw === "prominent") return raw;
  return "summary";
}

function orientationOf(raw: unknown): SectionOrientation {
  if (raw === "overview" || raw === "detail" || raw === "reference") return raw;
  return "detail";
}

/**
 * Narrow the backend's `source` value, or return `null` when the run said
 * nothing about it.
 *
 * A value this client does not recognise is dropped rather than coerced: the
 * one wrong answer here would be treating an unfamiliar state as accepted AI
 * prose, so an unknown state reads as "not narrated" instead.
 */
function narrationSourceOf(raw: unknown): NarrationSource | null {
  return raw === "ai" || raw === "fallback" || raw === "empty" ? raw : null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function stringOr(v: unknown, fallback: string): string {
  return typeof v === "string" && v.length > 0 ? v : fallback;
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function stringArray(v: unknown): readonly string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function titleCase(s: string): string {
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
